import moment from 'jalali-moment'
import { DigitToMonth } from 'utilities/helper/digitToMonth'
import { fundTitle } from 'utilities/helper/format'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'

// «تراکنش‌های من»: every payment the member was on either end of, from every
// box they are in, wallet top-ups and plan upgrades included. One row is one
// `payment.Transaction`, so the words and the direction below are worked out
// from where the money went (`source` → `destination`) and whether the member
// was the one paying — the row itself carries no «in» or «out».

/**
 * Which way the money went, as the member would say it.
 *
 *  - `in`:         it reached their own bank account (a loan, a withdrawal).
 *  - `out`:        they paid it (an installment, a membership fee, an upgrade).
 *  - `wallet_in`:  «شارژ کیف پول» — money arrived in their Hamyan wallet.
 *  - `wallet_out`: «برداشت از کیف پول» — it left the wallet for their own bank
 *                  account. What leaves the wallet *for a box* is `out`: the
 *                  box is what the member is looking for on that row, and the
 *                  wallet is only how it got there (`hint`).
 *  - `move`:       it moved without leaving or reaching their pocket — a manager
 *                  paying another member out of the box, somebody else paying an
 *                  installment on their behalf, a box settling to its own account.
 */
export type TMyTransactionKind =
    | 'in'
    | 'out'
    | 'wallet_in'
    | 'wallet_out'
    | 'move'

/** `payment.Transaction.source` / `.destination` — `utils/constants/choice.py` */
const GATEWAY = 'gtw'
const WALLET = 'hwt'
const HAMYAN_PLAN = 'hpl'
const BOX_BALANCE = 'hbb'
const BOX_BANK_ACCOUNT = 'bba'
const MEMBER_BANK_ACCOUNT = 'mba'
const CASH = 'csh'

/** `payment.Transaction.ctx_type` */
const CTX_MONEYPOOL = 'mnp'
const CTX_CYCLE = 'ccl'
const CTX_ORDER = 'ord'

const BOX_SIDES = [BOX_BALANCE, BOX_BANK_ACCOUNT]

const LOAN = 'ctl'
const CUSTOM_CASHOUT = 'ctc'

export function kindOf(
    item: IDashboardTransactionResult,
    me: number | null
): TMyTransactionKind {
    const { source, destination } = item
    if (destination === WALLET) return 'wallet_in'
    if (destination === MEMBER_BANK_ACCOUNT) {
        // The manager who pays a loan out is the payer of that row too, so
        // «I paid» says nothing here: only the member it reached received it.
        if (item.receiver !== me) return 'move'
        // Out of their own wallet into their own bank account: nothing was
        // earned or spent, the same money changed pocket.
        return source === WALLET ? 'wallet_out' : 'in'
    }
    if (BOX_SIDES.includes(source) && BOX_SIDES.includes(destination))
        return 'move'
    if (destination === HAMYAN_PLAN) return 'out'
    if (BOX_SIDES.includes(destination))
        return item.payer === me ? 'out' : 'move'
    if (item.payer === me) return 'out'
    if (item.receiver === me) return 'in'
    return 'move'
}

/** «+» / «−», or nothing for a row that never touched the member's pocket. */
export function signOf(kind: TMyTransactionKind): '+' | '−' | '' {
    if (kind === 'in' || kind === 'wallet_in') return '+'
    if (kind === 'out' || kind === 'wallet_out') return '−'
    return ''
}

/** How the money travelled, drawn as a small mark beside the words for it. */
export type THintIcon = 'wallet' | 'gateway' | 'cash' | 'bank' | 'link'

export interface IMyTransactionHint {
    icon: THintIcon
    /** «برداشت از کیف پول», «با درگاه بانکی», «برای پرداخت به صندوق مهر» */
    label: string
}

export interface IMyTransactionView {
    kind: TMyTransactionKind
    /** «صندوق مهر», «کیف پول همیان», «ارتقای صندوق» */
    title: string
    /** what it was for: «پرداخت قسط», «شارژ کیف پول», «دریافت وام» */
    subtitle: string
    /** the other person in it, when there is one: «برای …», «به …», «پرداخت توسط …» */
    party: string
    /** which account it came out of, or what a top-up was spent on */
    hint: IMyTransactionHint | null
    /** the box's own page, when the row says which box */
    boxPath: string | null
}

const trimmed = (name: string | null | undefined) => (name ?? '').trim()

const toman = (amount: number) => `${amount.toLocaleString()} تومان`

/** «صندوق مهر» / «ارتقای صندوق» — what a row was for, without its links. */
function targetName(item: IDashboardTransactionResult): string {
    if (item.ctx_type === CTX_ORDER || item.destination === HAMYAN_PLAN)
        return 'ارتقای صندوق'
    if (item.moneypool_data)
        return fundTitle(item.moneypool_data.moneypool_name)
    if (item.cycle_data) return fundTitle(item.cycle_data.cashbox_name)
    return 'همیان'
}

function moneypoolSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    const type = item.moneypool_data?.type ?? ''
    if (kind === 'in') {
        if (type === LOAN) return 'دریافت وام'
        if (type === CUSTOM_CASHOUT) return 'برداشت از صندوق'
    }
    if (type && MoneyPoolTransactionTypes[type])
        return MoneyPoolTransactionTypes[type]
    // No cash-in or cash-out row behind it: the box's own money moving from
    // «موجودی نزد همیان» to the box's bank account.
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return item.moneypool_data?.is_cashin ? 'واریز به صندوق' : 'برداشت از صندوق'
}

function cycleSubtitle(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): string {
    if (item.destination === MEMBER_BANK_ACCOUNT)
        return kind === 'in' ? 'دریافت وام قرعه‌کشی' : 'پرداخت وام قرعه‌کشی'
    if (BOX_SIDES.includes(item.source) && BOX_SIDES.includes(item.destination))
        return 'انتقال به حساب صندوق'
    return 'پرداخت سهم'
}

/** «شارژ کیف پول» / «برگشت پول به کیف پول» — the two ways money gets in. */
function walletInSubtitle(item: IDashboardTransactionResult): string {
    if (item.source === GATEWAY) return 'شارژ کیف پول'
    if (BOX_SIDES.includes(item.source)) return 'برگشت پول به کیف پول'
    return 'واریز به کیف پول'
}

function partyOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind,
    me: number | null
): string {
    const receiver = trimmed(item.receiver_name)
    const payer = trimmed(item.payer_name)
    if (item.destination === MEMBER_BANK_ACCOUNT) {
        return kind === 'move' && receiver ? `به ${receiver}` : ''
    }
    if (!BOX_SIDES.includes(item.destination)) return ''
    if (BOX_SIDES.includes(item.source)) return ''
    // Paid for somebody else, or by somebody else. The one who pays for a
    // whole family is exactly who needs to tell these rows apart.
    if (kind === 'out' && item.receiver !== null && item.receiver !== me)
        return receiver ? `برای ${receiver}` : ''
    if (kind === 'move' && item.payer !== me)
        return payer ? `پرداخت توسط ${payer}` : ''
    return ''
}

/**
 * «برای پرداخت به صندوق مهر» — what a top-up was handed straight on to. Said
 * on the top-up row so the «+» and the «−» under it read as one payment.
 */
function spentOnLabel(spent: NonNullable<IWalletLink['spentOn']>): string {
    return spent.count > 1
        ? `برای ${spent.count} پرداخت در همان لحظه`
        : `برای پرداخت به ${spent.titles[0]}`
}

function hintOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind,
    me: number | null,
    link?: IWalletLink
): IMyTransactionHint | null {
    if (kind === 'wallet_in') {
        if (link?.spentOn)
            return { icon: 'link', label: spentOnLabel(link.spentOn) }
        if (item.source === GATEWAY)
            return { icon: 'gateway', label: 'با درگاه بانکی' }
        if (BOX_SIDES.includes(item.source))
            return { icon: 'bank', label: 'از موجودی صندوق' }
        return null
    }
    if (kind === 'out') {
        // Everything a member pays runs through the wallet, so «برداشت از کیف
        // پول» is the one line that answers «this money came from where?».
        if (item.source === WALLET && item.payer === me)
            return { icon: 'wallet', label: 'برداشت از کیف پول' }
        if (item.source === GATEWAY)
            return { icon: 'gateway', label: 'با درگاه بانکی' }
        if (item.source === CASH)
            return { icon: 'cash', label: 'پرداخت نقدی، ثبت مدیر' }
        return null
    }
    if (kind === 'in' || kind === 'wallet_out')
        return { icon: 'bank', label: 'به حساب بانکی شما' }
    return null
}

export function describeTransaction(
    item: IDashboardTransactionResult,
    me: number | null,
    link?: IWalletLink
): IMyTransactionView {
    const kind = kindOf(item, me)
    const party = partyOf(item, kind, me)
    const hint = hintOf(item, kind, me, link)

    if (kind === 'wallet_in' || kind === 'wallet_out') {
        return {
            kind,
            title: 'کیف پول همیان',
            subtitle:
                kind === 'wallet_out'
                    ? 'برداشت از کیف پول'
                    : walletInSubtitle(item),
            party: '',
            hint,
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_ORDER || item.destination === HAMYAN_PLAN) {
        return {
            kind,
            title: 'ارتقای صندوق',
            subtitle: 'خرید اشتراک صندوق',
            party: '',
            hint,
            boxPath: null,
        }
    }
    if (item.ctx_type === CTX_MONEYPOOL && item.moneypool_data) {
        return {
            kind,
            title: fundTitle(item.moneypool_data.moneypool_name),
            subtitle: moneypoolSubtitle(item, kind),
            party,
            hint,
            boxPath: `/fund/${item.moneypool_data.id}`,
        }
    }
    if (item.ctx_type === CTX_CYCLE && item.cycle_data) {
        const cashboxId = item.cycle_data.cashbox_id
        return {
            kind,
            title: fundTitle(item.cycle_data.cashbox_name),
            subtitle: cycleSubtitle(item, kind),
            party,
            hint,
            // Only a server that sends the cashbox's id can link to it — a
            // cycle's own id is not a page anyone can open.
            boxPath:
                typeof cashboxId === 'number' ? `/cashbox/${cashboxId}` : null,
        }
    }
    return {
        kind,
        title: 'تراکنش همیان',
        subtitle: kind === 'in' ? 'دریافت' : kind === 'out' ? 'پرداخت' : '',
        party,
        hint,
        boxPath: null,
    }
}

/** Whether the row belongs to a صندوق قرعه‌کشی rather than a قرض‌الحسنه. */
export const isCashboxRow = (item: IDashboardTransactionResult) =>
    item.ctx_type === CTX_CYCLE

/**
 * «از» / «به» on the details sheet, in the words the rest of the app uses for
 * the same places.
 */
export function placeLabel(
    place: string,
    item: IDashboardTransactionResult,
    me: number | null,
    side: 'from' | 'to'
): string {
    switch (place) {
        case GATEWAY:
            return 'درگاه بانکی'
        case WALLET:
            return 'کیف پول همیان'
        case BOX_BALANCE:
            return 'موجودی صندوق نزد همیان'
        case BOX_BANK_ACCOUNT:
            return 'حساب بانکی صندوق'
        case HAMYAN_PLAN:
            return 'همیان'
        case CASH:
            return 'نقدی'
        case MEMBER_BANK_ACCOUNT: {
            const owner = side === 'to' ? item.receiver : item.payer
            const name = trimmed(
                side === 'to' ? item.receiver_name : item.payer_name
            )
            if (owner === me) return 'حساب بانکی شما'
            return name ? `حساب بانکی ${name}` : 'حساب بانکی عضو'
        }
        default:
            return ''
    }
}

/**
 * What left the member's pocket: the amount and the commission charged on
 * top of it. Only for what they paid themselves — the commission on a loan
 * paid out to them is the box's, not theirs.
 */
export function chargedTotal(item: IDashboardTransactionResult): number {
    return (item.amount ?? 0) + (item.total_commission ?? 0)
}

/**
 * The commission the member paid on a row, or 0 on one they did not pay: on a
 * loan paid out to them it is the box's cost, not theirs.
 */
export function commissionOf(
    item: IDashboardTransactionResult,
    kind: TMyTransactionKind
): number {
    const commission = item.total_commission ?? 0
    return kind === 'out' && commission > 0 ? commission : 0
}

// --- the wallet as a corridor -------------------------------------------------

// Paying a box by gateway is booked as two rows, not one: `GatewayPayment.
// make_successful` first moves the whole basket into the member's wallet
// (`gtw → hwt`), then each item out of it (`hwt → hbb` / `bba` / `hpl`). The
// member asked for neither — they tapped «پرداخت» once — and what they see is
// a «+۵,۰۰۰» right next to a «−۵,۰۰۰», which reads like being charged twice.
//
// On 7 days of production: 1,054 of 1,152 wallet payments have a top-up right
// before them, and for 598 of 742 top-ups the payments under it add up to
// exactly the top-up. So the pair can be spotted here and said out loud.

/** How close a payment has to follow a top-up to be the same basket. */
const PASS_THROUGH_WINDOW_SECONDS = 180

export interface IWalletLink {
    /** the top-up this payment was handed straight out of */
    fundedBy?: { pk: number; amount: number }
    /** the payments this top-up was handed straight on to */
    spentOn?: { titles: string[]; count: number; total: number }
}

const isTopUp = (item: IDashboardTransactionResult, me: number | null) =>
    item.source === GATEWAY &&
    item.destination === WALLET &&
    (item.payer === me || item.receiver === me)

const isPaidFromWallet = (
    item: IDashboardTransactionResult,
    me: number | null
) => item.source === WALLET && item.payer === me && kindOf(item, me) === 'out'

/**
 * Which rows are the two halves of one payment. The list is newest first, so
 * the payments of a basket sit *above* the top-up that funded them; a top-up
 * takes the payments right after it whose totals still fit inside it, and each
 * payment is claimed once.
 */
export function linkWalletRows(
    rows: IDashboardTransactionResult[],
    me: number | null
): Map<number, IWalletLink> {
    const links = new Map<number, IWalletLink>()
    if (me === null) return links

    interface IPending {
        row: IDashboardTransactionResult
        at: number
        total: number
    }
    let pending: IPending[] = []

    rows.forEach((row) => {
        const at = tehranMoment(row)?.valueOf()
        if (at === undefined) return

        if (isPaidFromWallet(row, me)) {
            pending.push({ row, at, total: chargedTotal(row) })
            return
        }
        if (!isTopUp(row, me)) return

        const amount = row.amount ?? 0
        const taken: IPending[] = []
        let total = 0
        pending.forEach((candidate) => {
            // A payment out of the basket is booked after the top-up, within
            // seconds of it, and the basket cannot cost more than was put in.
            if (candidate.row.pk <= row.pk) return
            const gap = (candidate.at - at) / 1000
            if (gap < 0 || gap > PASS_THROUGH_WINDOW_SECONDS) return
            if (total + candidate.total > amount) return
            total += candidate.total
            taken.push(candidate)
        })
        if (!taken.length) return

        const titles: string[] = []
        taken.forEach((candidate) => {
            links.set(candidate.row.pk, {
                ...links.get(candidate.row.pk),
                fundedBy: { pk: row.pk, amount },
            })
            const title = targetName(candidate.row)
            if (!titles.includes(title)) titles.push(title)
        })
        pending = pending.filter((candidate) => !taken.includes(candidate))
        links.set(row.pk, {
            ...links.get(row.pk),
            spentOn: { titles, count: taken.length, total },
        })
    })

    return links
}

// --- «این تراکنش چه بود؟» -----------------------------------------------------

/**
 * The one thing a row does not say by itself, in a sentence — or nothing, when
 * the header above it (the amount, the box, «با درگاه بانکی») already says it
 * all. Mostly that is the wallet corridor: a «+» beside a «−» reads like being
 * charged twice. The commission is not repeated here; the header breaks the
 * amount down into the payment, its commission and what they come to.
 */
export function explainTransaction(
    item: IDashboardTransactionResult,
    view: IMyTransactionView,
    me: number | null,
    link?: IWalletLink,
    options: {
        /**
         * The list is filtered down to a box or a kind of payment, so the
         * wallet top-ups that paid for its rows are not in it (see
         * `hidesTopUps`).
         */
        topUpsHidden?: boolean
    } = {}
): string {
    const amount = item.amount ?? 0

    if (view.kind === 'wallet_in') {
        const spent = link?.spentOn
        if (!spent)
            return 'این مبلغ در کیف پول همیان شما مانده و در هر صندوقی می‌توانید با آن پرداخت کنید.'
        // A basket is several rows, not one row of the total: count them.
        const target =
            spent.count > 1
                ? `${spent.count} پرداخت`
                : `پرداخت به ${spent.titles[0]}`
        // Only a basket that used the whole top-up leaves the balance where
        // it was; whatever was left over stayed in the wallet.
        return amount > spent.total
            ? `${toman(
                  spent.total
              )} از آن همان لحظه صرف ${target} شد و باقیِ این شارژ در کیف پول شما ماند.`
            : `همان لحظه صرف ${target} شد؛ پرداخت دوباره نیست و موجودی کیف پول شما دست‌نخورده مانده است.`
    }

    if (view.kind === 'wallet_out')
        return 'از کیف پول به حساب بانکی خودتان برگشت و از سرمایهٔ شما در صندوق‌ها کم نمی‌کند.'

    if (view.kind === 'in') {
        const type = item.moneypool_data?.type ?? ''
        if (type === LOAN || item.ctx_type === CTX_CYCLE)
            return 'این مبلغ وام است و در قسط‌های تعیین‌شده بازپرداخت می‌شود.'
        if (type === CUSTOM_CASHOUT)
            return 'این برداشت از سرمایهٔ شما در صندوق است.'
        return ''
    }

    if (view.kind === 'out') {
        if (item.source !== WALLET) return ''
        if (link?.fundedBy) {
            // The top-up is older, so it is *below* this row, and one top-up
            // can cover a whole basket: «همین پرداخت» is only true when it
            // paid for this row alone.
            const charge = link.fundedBy.amount
            // Isolated LTR, or the RTL sentence paints the «+» on the digits'
            // right — «۶۰,۰۰۰+» — and it no longer reads like the row.
            const where = `ردیف «\u2066+${charge.toLocaleString()}\u2069 تومان» پایین‌تر در همین فهرست`
            return charge === chargedTotal(item)
                ? `${where} شارژ کیف پول برای همین پرداخت است؛ این دو ردیف یک پرداخت‌اند، نه دو پرداخت.`
                : `${where} شارژی بود که این و چند پرداخت دیگر را با هم پوشاند؛ پرداخت جدایی نیست.`
        }
        // Its half of a gateway payment is filtered out, so the pair can not
        // be pointed at — only said.
        return options.topUpsHidden
            ? 'اگر با درگاه بانکی پرداخت کرده‌اید، ردیف «شارژ کیف پول» آن با این فیلتر نمایش داده نمی‌شود؛ آن دو یک پرداخت‌اند.'
            : ''
    }

    // A move: the row is in the member's list because they were on one end of
    // it, but their own pocket was not that end.
    const payer = trimmed(item.payer_name)
    const receiver = trimmed(item.receiver_name)
    if (
        BOX_SIDES.includes(item.source) &&
        BOX_SIDES.includes(item.destination)
    ) {
        return 'پول خودِ صندوق به حساب بانکی صندوق منتقل شده و به حساب شما کاری ندارد.'
    }
    if (item.destination === MEMBER_BANK_ACCOUNT) {
        const to = receiver ? `«${receiver}»` : 'یکی از اعضا'
        return `این پرداخت از صندوق به ${to} را شما ثبت کرده‌اید؛ از حساب شما چیزی کم نشده است.`
    }
    if (item.payer !== me && payer) {
        return `«${payer}» این مبلغ را برای شما پرداخت کرده؛ به سرمایهٔ شما اضافه می‌شود ولی از جیب شما کم نشده است.`
    }
    return 'این ردیف از حساب شما چیزی کم یا زیاد نکرده است.'
}

// Iran dropped daylight saving in 1401, so Tehran is a fixed +03:30. The same
// constant as the fund tab's `days.ts`.
const TEHRAN_UTC_OFFSET_MINUTES = 210

/**
 * When it happened, on Tehran's clock. `state_time` is when the payment
 * settled — the moment the member saw it go through — and it arrives as
 * «2026-09-21 18:15:02+03:30»; `created` is the fallback for a row that never
 * got one.
 */
export function tehranMoment(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): moment.Moment | null {
    for (const stamp of [item.state_time, item.created]) {
        if (!stamp) continue
        const parsed = moment(stamp.replace(' ', 'T'), moment.ISO_8601)
        if (parsed.isValid()) return parsed.utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
    }
    return null
}

/** «1405-06-30», the key `describeDay` reads — '' when there is no date. */
export function jalaliDayKey(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>
): string {
    const at = tehranMoment(item)
    return at ? at.format('jYYYY-jMM-jDD') : ''
}

/** «۱۸:۱۵», or «۱۸:۱۵:۰۲» for the details sheet. */
export function clockTime(
    item: Pick<IDashboardTransactionResult, 'state_time' | 'created'>,
    withSeconds = false
): string {
    const at = tehranMoment(item)
    return at ? at.format(withSeconds ? 'HH:mm:ss' : 'HH:mm') : ''
}

export interface ITransactionDay<T> {
    key: string
    rows: T[]
}

/**
 * Runs of the same day, in the order the server sent them. Runs, not buckets:
 * the list is newest-first by id, and re-sorting it here would quietly
 * disagree with the order it pages in.
 */
export function runsByDay<T extends IDashboardTransactionResult>(
    rows: T[]
): ITransactionDay<T>[] {
    const days: ITransactionDay<T>[] = []
    rows.forEach((row) => {
        const key = jalaliDayKey(row)
        const last = days[days.length - 1]
        if (last && last.key === key) last.rows.push(row)
        else days.push({ key, rows: [row] })
    })
    return days
}

// --- «بازهٔ زمانی» -----------------------------------------------------------

export type TPeriod =
    | ''
    | 'this_month'
    | 'last_month'
    | 'three_months'
    | 'this_year'

export const PERIOD_OPTIONS: { key: TPeriod; label: string; aria?: string }[] =
    [
        { key: '', label: 'همه', aria: 'همهٔ تراکنش‌ها' },
        { key: 'this_month', label: 'این ماه' },
        { key: 'last_month', label: 'ماه گذشته' },
        { key: 'three_months', label: 'سه ماه اخیر' },
        { key: 'this_year', label: 'امسال' },
    ]

export interface IPeriodRange {
    /** 'jYYYY/jMM/jDD', both ends included */
    from: string
    to: string
}

const RANGE_FORMAT = 'jYYYY/jMM/jDD'

function tehranToday(now: Date) {
    return moment(now).utcOffset(TEHRAN_UTC_OFFSET_MINUTES)
}

/**
 * The two Jalali days a period covers, both included. The server takes them
 * as `date=1405/06/01,1405/06/31` and reads the second one as the whole day.
 */
export function periodRange(
    period: TPeriod,
    now: Date = new Date()
): IPeriodRange | null {
    if (period === '') return null
    const today = tehranToday(now)
    const format = (at: moment.Moment) => at.format(RANGE_FORMAT)
    switch (period) {
        case 'this_month':
            return {
                from: format(today.clone().startOf('jMonth')),
                to: format(today),
            }
        case 'last_month': {
            const lastMonth = today.clone().startOf('jMonth').subtract(1, 'day')
            return {
                from: format(lastMonth.clone().startOf('jMonth')),
                to: format(lastMonth),
            }
        }
        case 'three_months':
            return {
                from: format(today.clone().subtract(3, 'jMonth').add(1, 'day')),
                to: format(today),
            }
        case 'this_year':
            return {
                from: format(today.clone().startOf('jYear')),
                to: format(today),
            }
        default:
            return null
    }
}

/** The `date=` the server expects, or undefined for «همه». */
export function periodParam(
    period: TPeriod,
    now: Date = new Date()
): string | undefined {
    const range = periodRange(period, now)
    return range ? `${range.from},${range.to}` : undefined
}

/**
 * «۱ شهریور تا ۲۶ شهریور ۱۴۰۵» — the year said once when both ends share it,
 * which every period but «سه ماه اخیر» in spring does.
 */
export function periodRangeLabel(range: IPeriodRange): string {
    const from = moment(range.from, RANGE_FORMAT)
    const to = moment(range.to, RANGE_FORMAT)
    if (!from.isValid() || !to.isValid()) return ''
    const day = (at: moment.Moment) =>
        `${at.jDate()} ${DigitToMonth[at.jMonth() + 1]}`
    return from.jYear() === to.jYear()
        ? `${day(from)} تا ${day(to)} ${to.jYear()}`
        : `${day(from)} ${from.jYear()} تا ${day(to)} ${to.jYear()}`
}

// --- the period's totals -----------------------------------------------------

export interface IPeriodTotals {
    received: number
    paid: number
    wallet: number
    count: number
}

/**
 * What came in, what went out and what is sitting in the wallet, over the rows
 * given. «پرداختی» counts what left the member's pocket — the commission
 * included — because that is the figure a bank statement will show them.
 *
 * A top-up that was handed straight on to a box is *not* counted as a top-up:
 * the payment under it is already in «پرداختی», and counting both would tell
 * the member they spent the same money twice.
 */
export function periodTotals(
    rows: IDashboardTransactionResult[],
    me: number | null,
    links?: Map<number, IWalletLink>
): IPeriodTotals {
    return rows.reduce<IPeriodTotals>(
        (totals, row) => {
            const kind = kindOf(row, me)
            if (kind === 'in') totals.received += row.amount ?? 0
            else if (kind === 'out') totals.paid += chargedTotal(row)
            else if (kind === 'wallet_in' && !links?.get(row.pk)?.spentOn)
                totals.wallet += row.amount ?? 0
            totals.count += 1
            return totals
        },
        { received: 0, paid: 0, wallet: 0, count: 0 }
    )
}

// --- «فیلتر»: صندوق، نوع تراکنش، بازهٔ دلخواه ---------------------------------

// Everything below is kept in the page's URL — `box_type` + `box_id`, `type`,
// `from` + `to` — so going back to the list, or reloading it, finds it the way
// it was left. What goes to the server is worked out from it (`filterParams`).

export type TFilterBoxType = 'moneypool' | 'cashbox'

export interface IFilterBox {
    type: TFilterBoxType
    id: number
}

export type TFilterKind =
    | 'installment'
    | 'membership'
    | 'deposit'
    | 'hibah'
    | 'cashbox_share'
    | 'loan'
    | 'withdrawal'
    | 'wallet'
    | 'upgrade'

export interface IKindOption {
    key: TFilterKind
    label: string
    /** the server's `kind=` codes — the ones its rows already carry */
    codes: string[]
    /** the kinds of box it happens in; none: it never belongs to a box */
    boxTypes: TFilterBoxType[]
    /**
     * Something the member pays, so something a gateway payment's wallet
     * top-up may have paid for — the top-up that this kind leaves out.
     */
    isPayment: boolean
}

export const KIND_OPTIONS: IKindOption[] = [
    {
        key: 'installment',
        label: 'پرداخت قسط',
        codes: ['cnr'],
        boxTypes: ['moneypool'],
        isPayment: true,
    },
    {
        key: 'membership',
        label: 'حق عضویت',
        codes: ['cnp', 'cnpr'],
        boxTypes: ['moneypool'],
        isPayment: true,
    },
    {
        key: 'deposit',
        label: 'افزایش موجودی',
        codes: ['cnc'],
        boxTypes: ['moneypool'],
        isPayment: true,
    },
    {
        key: 'hibah',
        label: 'هبه',
        codes: ['cnhe', 'cnhc'],
        boxTypes: ['moneypool'],
        isPayment: true,
    },
    {
        key: 'cashbox_share',
        label: 'سهم قرعه‌کشی',
        codes: ['cns'],
        boxTypes: ['cashbox'],
        isPayment: true,
    },
    {
        // A loan out of a قرض‌الحسنه and what a قرعه‌کشی pays its winner.
        key: 'loan',
        label: 'وام',
        codes: ['ctl'],
        boxTypes: ['moneypool', 'cashbox'],
        isPayment: false,
    },
    {
        key: 'withdrawal',
        label: 'برداشت از صندوق',
        codes: ['ctc'],
        boxTypes: ['moneypool'],
        isPayment: false,
    },
    {
        // Into or out of the wallet: both halves of a gateway payment, so
        // the pair stays together. Under a box, only the half in that box.
        key: 'wallet',
        label: 'کیف پول',
        codes: ['hwt'],
        boxTypes: ['moneypool', 'cashbox'],
        isPayment: false,
    },
    {
        key: 'upgrade',
        label: 'ارتقای صندوق',
        codes: ['hpl'],
        boxTypes: [],
        isPayment: true,
    },
]

export const kindOption = (key: TFilterKind | null | undefined) =>
    KIND_OPTIONS.find((option) => option.key === key)

/** The kinds a box of this type can have — all of them, with no box. */
export function kindOptionsFor(boxType: TFilterBoxType | null): IKindOption[] {
    return boxType
        ? KIND_OPTIONS.filter((option) => option.boxTypes.includes(boxType))
        : KIND_OPTIONS
}

export interface ITransactionFilters {
    box: IFilterBox | null
    kind: TFilterKind | null
    /** 'jYYYY-jMM-jDD', both ends included; either end may be open */
    from: string | null
    to: string | null
}

export const NO_FILTERS: ITransactionFilters = {
    box: null,
    kind: null,
    from: null,
    to: null,
}

const DAY_FORMAT = 'jYYYY-jMM-jDD'

/**
 * «1405-06-01» and nothing else: not «1405-6-1», and not «1405-07-31», which
 * jalali-moment would quietly roll over into «1405-08-01».
 */
export function isJalaliDay(value: string | null | undefined): value is string {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
    const day = moment(value, DAY_FORMAT)
    return day.isValid() && day.format(DAY_FORMAT) === value
}

/**
 * The Gregorian 'YYYY-MM-DD' the server's `date_from` / `date_to` take. Worked
 * out here, with jalali-moment — the calendar every Jalali date in the PWA
 * goes through, and the one that agrees with the server's jdatetime.
 */
export const jalaliDayToIso = (day: string) =>
    moment(day, DAY_FORMAT).format('YYYY-MM-DD')

/** A day picked on the calendar, as the URL keeps it. */
export const dateToJalaliDay = (date: Date) => moment(date).format(DAY_FORMAT)

export const jalaliDayToDate = (day: string) =>
    moment(day, DAY_FORMAT).toDate()

const BOX_TYPES: TFilterBoxType[] = ['moneypool', 'cashbox']

/** The filters in a URL. Anything it cannot read is left out, not guessed. */
export function readFilters(params: URLSearchParams): ITransactionFilters {
    const boxType = params.get('box_type') as TFilterBoxType
    const boxId = Number(params.get('box_id'))
    const box =
        BOX_TYPES.includes(boxType) && Number.isInteger(boxId) && boxId > 0
            ? { type: boxType, id: boxId }
            : null

    // A kind the box can never have would only ever list nothing.
    const option = KIND_OPTIONS.find((each) => each.key === params.get('type'))
    const kind =
        option && (!box || option.boxTypes.includes(box.type))
            ? option.key
            : null

    const from = params.get('from')
    const to = params.get('to')
    const first = isJalaliDay(from) ? from : null
    const last = isJalaliDay(to) ? to : null
    // Same zero-padded format on both ends, so string order is day order.
    const swapped = !!first && !!last && first > last
    return {
        box,
        kind,
        from: swapped ? last : first,
        to: swapped ? first : last,
    }
}

/**
 * The URL with these filters, whatever else it carries kept. A range of days
 * takes the place of a period rather than narrowing it.
 */
export function writeFilters(
    params: URLSearchParams,
    filters: ITransactionFilters
): URLSearchParams {
    const next = new URLSearchParams(params)
    const put = (key: string, value: string | null | undefined) => {
        if (value) next.set(key, value)
        else next.delete(key)
    }
    put('box_type', filters.box?.type)
    put('box_id', filters.box ? String(filters.box.id) : null)
    put('type', filters.kind)
    put('from', filters.from)
    put('to', filters.to)
    if (filters.from || filters.to) next.delete('period')
    return next
}

/** What the server is asked for. */
export function filterParams(
    filters: ITransactionFilters
): Pick<
    IDashboardTransactionsParams,
    'box_type' | 'box_id' | 'kind' | 'date_from' | 'date_to'
> {
    return {
        box_type: filters.box?.type,
        box_id: filters.box?.id,
        kind: kindOption(filters.kind)?.codes.join(','),
        date_from: filters.from ? jalaliDayToIso(filters.from) : undefined,
        date_to: filters.to ? jalaliDayToIso(filters.to) : undefined,
    }
}

export type TFilterName = 'box' | 'kind' | 'date'

export function activeFilters(filters: ITransactionFilters): TFilterName[] {
    const names: TFilterName[] = []
    if (filters.box) names.push('box')
    if (filters.kind) names.push('kind')
    if (filters.from || filters.to) names.push('date')
    return names
}

export const sameFilters = (a: ITransactionFilters, b: ITransactionFilters) =>
    a.box?.type === b.box?.type &&
    a.box?.id === b.box?.id &&
    a.kind === b.kind &&
    a.from === b.from &&
    a.to === b.to

/**
 * Whether the wallet top-ups of gateway payments are filtered out of the list.
 *
 * A gateway payment is two rows (see `linkWalletRows`): the top-up belongs to
 * the member's wallet, not to any box, and one top-up can pay several boxes at
 * once. So a box, or a kind of payment, lists the payment and not the top-up —
 * adding the top-up to a box's list would also add its whole amount to what
 * that box seems to have been paid. The page says so instead of pairing rows
 * it cannot see.
 *
 * A loan or a withdrawal was never paid for by a top-up, and «کیف پول» without
 * a box keeps both halves together.
 */
export function hidesTopUps(filters: ITransactionFilters): boolean {
    const option = kindOption(filters.kind)
    if (filters.box)
        return !option || option.isPayment || option.key === 'wallet'
    return !!option && option.isPayment
}

const squeezed = (text: string) => text.replace(/[\s‌]/g, '')

const BOX_TYPE_TITLE: Record<TFilterBoxType, { title: string; word: string }> =
    {
        moneypool: { title: 'صندوق قرض‌الحسنه', word: 'قرضالحسنه' },
        cashbox: { title: 'صندوق قرعه‌کشی', word: 'قرعهکشی' },
    }

/**
 * «صندوق قرض‌الحسنه مهر» / «صندوق قرعه‌کشی امید»: in a list of both kinds of
 * box the kind is part of the name — unless the name already says it, as
 * «صندوق قرض الحسنه رز» does.
 */
export function typedFundTitle(
    name: string | null | undefined,
    type: TFilterBoxType
): string {
    const { title, word } = BOX_TYPE_TITLE[type]
    const trimmedName = (name ?? '').trim()
    if (squeezed(trimmedName).includes(word)) return fundTitle(trimmedName)
    const bare = trimmedName.replace(/^صندوق[\s‌]*/, '').trim()
    return bare ? `${title} ${bare}` : title
}

export interface IFundOption {
    box: IFilterBox
    title: string
}

interface INamedBox {
    id: number
    name: string
}

/** The member's boxes, both kinds, as `/general/boxes/` lists them. */
export function fundOptions(
    boxes:
        | { moneypools?: INamedBox[]; cashboxes?: INamedBox[] }
        | null
        | undefined
): IFundOption[] {
    const options: IFundOption[] = []
    boxes?.moneypools?.forEach((box) =>
        options.push({
            box: { type: 'moneypool', id: box.id },
            title: typedFundTitle(box.name, 'moneypool'),
        })
    )
    boxes?.cashboxes?.forEach((box) =>
        options.push({
            box: { type: 'cashbox', id: box.id },
            title: typedFundTitle(box.name, 'cashbox'),
        })
    )
    return options
}

/**
 * A box's name read off the rows already listed — for a box that is not in
 * the member's list of boxes (any more), or before that list has arrived.
 */
export function boxTitleFromRows(
    rows: IDashboardTransactionResult[] | undefined,
    box: IFilterBox
): string | null {
    for (const item of rows ?? []) {
        if (box.type === 'moneypool' && item.moneypool_data?.id === box.id)
            return typedFundTitle(item.moneypool_data.moneypool_name, box.type)
        if (box.type === 'cashbox' && item.cycle_data?.cashbox_id === box.id)
            return typedFundTitle(item.cycle_data.cashbox_name, box.type)
    }
    return null
}

const longDay = (day: string) => {
    const at = moment(day, DAY_FORMAT)
    return `${at.jDate()} ${DigitToMonth[at.jMonth() + 1]} ${at.jYear()}`
}

/** «۱ شهریور تا ۲۶ شهریور ۱۴۰۵», «از ۱ شهریور ۱۴۰۵», «تا ۲۶ شهریور ۱۴۰۵». */
export function dayRangeLabel(from: string | null, to: string | null): string {
    if (from && to) {
        if (from === to) return longDay(from)
        return periodRangeLabel({
            from: from.replace(/-/g, '/'),
            to: to.replace(/-/g, '/'),
        })
    }
    if (from) return `از ${longDay(from)}`
    if (to) return `تا ${longDay(to)}`
    return ''
}

/** How many days a closed range covers, both ends counted. */
export function rangeDays(filters: ITransactionFilters): number | null {
    if (!filters.from || !filters.to) return null
    return (
        moment(filters.to, DAY_FORMAT).diff(
            moment(filters.from, DAY_FORMAT),
            'days'
        ) + 1
    )
}

/** Which filters, for PostHog — never the box's id or name. */
export function filterAnalytics(filters: ITransactionFilters) {
    const names = activeFilters(filters)
    return {
        filters: names,
        filter_count: names.length,
        box_type: filters.box?.type ?? null,
        kind: filters.kind,
        has_date_from: !!filters.from,
        has_date_to: !!filters.to,
        range_days: rangeDays(filters),
    }
}
